import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gridDates } from '../lib/months';
import { todayInZurich } from '../lib/today';

export type CellRenderArgs = {
  date: string;
  inMonth: boolean;
  isToday: boolean;
};

export type CalendarGridProps = {
  /** YYYY-MM */
  month: string;
  cellRenderer: (args: CellRenderArgs) => React.ReactNode;
  onDateClick?: (date: string) => void;
  /** ISO language tag, e.g. 'de' or 'en'. Defaults to i18n's resolved lang. */
  locale?: string;
};

export function CalendarGrid({ month, cellRenderer, onDateClick, locale }: CalendarGridProps) {
  const { i18n } = useTranslation();
  const lang = locale ?? i18n.resolvedLanguage ?? 'de';

  const cells = useMemo(() => gridDates(month), [month]);
  const today = todayInZurich();

  // Monday-first weekday labels in the active locale.
  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short' });
    // 2024-01-01 was a Monday; use that as the anchor.
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(Date.UTC(2024, 0, 1 + i))),
    );
  }, [lang]);

  return (
    <div className="select-none">
      <div className="grid grid-cols-7 gap-1 mb-1 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
        {weekdayLabels.map((w, i) => (
          <div key={i} className="text-center py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map(({ date, inMonth }) => {
          const isToday = date === today;
          const content = cellRenderer({ date, inMonth, isToday });
          const dayNum = Number(date.slice(8, 10));
          const Comp = onDateClick ? 'button' : 'div';
          return (
            <Comp
              key={date}
              type={onDateClick ? 'button' : undefined}
              onClick={onDateClick ? () => onDateClick(date) : undefined}
              aria-current={isToday ? 'date' : undefined}
              className={
                'relative aspect-square sm:aspect-[4/3] rounded-md border text-left p-1 sm:p-1.5 ' +
                'flex flex-col justify-between min-h-[44px] text-xs sm:text-sm ' +
                (inMonth
                  ? 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
                  : 'border-transparent bg-transparent text-slate-300 dark:text-slate-700') +
                (onDateClick ? ' hover:ring-2 hover:ring-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:hover:ring-slate-600 dark:focus:ring-slate-400' : '')
              }
            >
              <span
                className={
                  'relative z-10 leading-none ' +
                  (isToday
                    ? 'inline-flex items-center justify-center rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 w-5 h-5 sm:w-6 sm:h-6 text-[10px] sm:text-xs font-semibold'
                    : 'text-slate-500 dark:text-slate-400 font-medium')
                }
              >
                {dayNum}
              </span>
              <div className="mt-auto">{content}</div>
            </Comp>
          );
        })}
      </div>
    </div>
  );
}
